// redux/Menu.js (or store/menuSlice.js)
import { createSlice } from '@reduxjs/toolkit';
import { getMediaURL } from '../../../context/API';

const initialState = {
    categories: [],
    categoryItemsMap: {},
    openedCategories: [],
    loading: false,
    error: null,
    page: 1,
    hasMore: true,
    totalPages: 1,
    query: "",
};

const menuSlice = createSlice({
    name: 'menu',
    initialState,
    reducers: {
        setCategories: (state, action) => {
            const { data, meta, page, append = false } = action.payload;
            
            const categoriesData = data.map((category) => {
                const itemsData = (category.items || []).map((item) => ({
                    ...item,
                    picture: getMediaURL(item.picture),
                }));
                itemsData.sort((a, b) => a.sequence_number - b.sequence_number);

                return {
                    ...category,
                    picture: getMediaURL(category.picture),
                    items: itemsData.map((item) => item.id.toString()),
                    itemsData,
                };
            });

            // Update categoryItemsMap efficiently
            if (!append) {
                // Clear existing map for new data
                state.categoryItemsMap = {};
            }
            
            categoriesData.forEach((category) => {
                state.categoryItemsMap[category.id] = category.itemsData || [];
            });

            // Update categories
            if (append) {
                state.categories = [...state.categories, ...categoriesData];
            } else {
                state.categories = categoriesData;
            }

            // Optimized pagination logic
            state.totalPages = meta?.totalPages || 1;
            state.page = page;
            state.hasMore = page < (meta?.totalPages || 1) && data.length > 0;
        },

        setCategoryItems: (state, action) => {
            const { categoryId, items } = action.payload;
            const itemsData = Array.isArray(items)
                ? items.map((item) => ({
                    ...item,
                    picture: getMediaURL(item.picture),
                }))
                : [];

            itemsData.sort((a, b) => a.sequence_number - b.sequence_number);
            state.categoryItemsMap[categoryId] = itemsData;
        },

        updateItem: (state, action) => {
            const updatedItem = action.payload;

            // Find which category this item belongs to and update it
            for (const categoryId in state.categoryItemsMap) {
                const items = state.categoryItemsMap[categoryId];
                const itemIndex = items.findIndex(item => item.id === updatedItem.id);

                if (itemIndex !== -1) {
                    const existingItem = state.categoryItemsMap[categoryId][itemIndex];

                    // Update the item with new data while preserving customizations if not provided
                    state.categoryItemsMap[categoryId][itemIndex] = {
                        ...existingItem, // Keep existing data
                        ...updatedItem, // Override with new data
                        // Preserve customizations if they're not in the update
                        customizations: updatedItem.customizations || existingItem.customizations || [],
                        picture: updatedItem.picture ? getMediaURL(updatedItem.picture) : existingItem.picture
                    };

                    break;
                }
            }
        },

        addItem: (state, action) => {
            const { item, categoryId } = action.payload;
            const newItem = {
                ...item,
                picture: getMediaURL(item.picture),
            };

            if (state.categoryItemsMap[categoryId]) {
                state.categoryItemsMap[categoryId].push(newItem);
                // Sort by sequence number
                state.categoryItemsMap[categoryId].sort((a, b) => a.sequence_number - b.sequence_number);
            }
        },

        deleteItem: (state, action) => {
            const itemId = action.payload;

            // Remove item from all categories
            for (const categoryId in state.categoryItemsMap) {
                state.categoryItemsMap[categoryId] = state.categoryItemsMap[categoryId].filter(
                    item => item.id !== itemId
                );
            }
        },

        toggleCategory: (state, action) => {
            const categoryId = action.payload;
            if (state.openedCategories.includes(categoryId)) {
                state.openedCategories = state.openedCategories.filter(id => id !== categoryId);
            } else {
                state.openedCategories.push(categoryId);
            }
        },

        setOpenedCategories: (state, action) => {
            state.openedCategories = action.payload;
        },

        reorderCategories: (state, action) => {
            const newOrder = action.payload;
            const reorderedCategories = newOrder.map((id, index) => {
                const category = state.categories.find((cat) => cat.id === id);
                return {
                    ...category,
                    sequence_number: index + 1,
                };
            });
            state.categories = reorderedCategories;
        },

        reorderItems: (state, action) => {
            const { categoryId, newOrder } = action.payload;
            state.categoryItemsMap[categoryId] = newOrder;
        },

        setLoading: (state, action) => {
            state.loading = action.payload;
        },

        setError: (state, action) => {
            state.error = action.payload;
        },

        setQuery: (state, action) => {
            state.query = action.payload;
        },

        setPage: (state, action) => {
            state.page = action.payload;
        },

        resetMenu: (state) => {
            return initialState;
        },

        forceHasMoreFalse: (state) => {
            state.hasMore = false;
            state.page = 1; // Reset to first page
        },
    },
});

export const {
    setCategories,
    setCategoryItems,
    updateItem,
    addItem,
    deleteItem,
    toggleCategory,
    setOpenedCategories,
    reorderCategories,
    reorderItems,
    setLoading,
    setError,
    setQuery,
    setPage,
    resetMenu,
    forceHasMoreFalse, // ✅ Add this
} = menuSlice.actions;

export default menuSlice.reducer;
